import React, { useContext, useEffect, useRef, useState } from 'react';
import { IoDownload,IoCloudUpload } from "react-icons/io5";
import { Dialog } from "primereact/dialog";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { MdDelete } from "react-icons/md";
import styles from "./DownloadForms.module.css";
import { IoMdDownload } from "react-icons/io";
import { getFileSize } from '../../Util/utility';
import PageHeading from '../../Util/PageHeading';
import { columnProps, tablePassThrough, useTableScrollRegion } from '../../Util/dataTable';
import axios from 'axios';
import AuthContext from '../../../store/auth-context';
import { GlobalContext } from '../../../store/global-context';
import { FaFilePdf } from "react-icons/fa6";
import { useNavigate } from 'react-router-dom';
import { FaFileExcel } from "react-icons/fa";
import { IoDocumentText } from "react-icons/io5";
import CustomCard from "../../Util/CustomCard.jsx";
import { InputTextarea } from "primereact/inputtextarea";
import { FloatLabel } from "primereact/floatlabel";
import { errorMessage } from "../../Util/errorMessage";
import { useDialogIsolation } from "../../Util/useDialogIsolation";
import { htmlEdition } from "./htmlEditions";
import { busyButtonProps } from "../../Util/busyButton";
import { saveFile } from "../../Util/download";

/**
 * RCMC-12 (ticket 14): a download button's name — what it fetches, in what format, how big:
 * "Download <document name> (<FORMAT>, <size>)", e.g. "Download Form ISR-1.pdf (PDF, 983 Kb)". The format is the
 * name's extension (Upload Docs takes any file); a name without one ("Report v1.2" ends in a version number, not an
 * extension) gives the size alone. The name and size are the row's own Name and Size, as the Investor sees them.
 */
export function downloadButtonName({ document_name, document_size }) {
  const format = /\.([A-Za-z][A-Za-z0-9]{0,4})$/.exec(document_name)?.[1].toUpperCase();
  const facts = [format, document_size].filter(Boolean).join(", ");
  return facts ? `Download ${document_name} (${facts})` : `Download ${document_name}`;
}

const DownloadForms = () => {
  const navigate = useNavigate();
  const dt = useRef(null);
  // RCMC-04 (ticket 16): the table's region is a Tab stop while the table is too wide for it
  useTableScrollRegion(dt);
  const authCtx = useContext(AuthContext);
  const globalCtx = useContext(GlobalContext);
  const inputRef = useRef();
  const [isAddNewModalVisible, setIsAddNewModalVisible] = useState(false);
  // RCMC-23: page behind inert while open, focus to the first field; RCMC-02: named close button
  const uploadDialogIsolation = useDialogIsolation(isAddNewModalVisible, "Upload Docs");
  const [selectedDocument, setSelectedDocument] = useState();
  const [selectedDocumentDescription, setSelectedDocumentDescription] = useState("");
  const [listOfDownloadForms,setListOfDownloadForms] = useState([]);
  const [selectedRowCount, setSelectedRowCount] = useState(10);

  useEffect(() => {
    axios.get("/api/user/download-form-view",{headers: {Authorization: "Bearer " + authCtx.authTokens.access}}).then(response => {
        setListOfDownloadForms(response.data.download_forms.map((item,index) => {return {...item,sno:index+1}}));
    }).catch((e) => {
        globalCtx.raiseAlert(errorMessage(e), "error");
    });
  },[])

  const uploadNewDocHandler =() => {
    const formData = new FormData();
    formData.append("file",selectedDocument);
    formData.append("size",getFileSize(selectedDocument));
    formData.append("description",selectedDocumentDescription);
    if(selectedDocumentDescription.trim().length === 0){
        globalCtx.raiseAlert("Description cannot be blank!","error");
        return;
    }
    axios.post("/api/user/download-form-view",formData,{headers: {"Content-Type": "multipart/form-data",Authorization: "Bearer " + authCtx.authTokens.access}}).then(response => {
        const newDoc = response.data.download_forms;
        newDoc['sno'] = listOfDownloadForms.length + 1;
        setListOfDownloadForms(prevState => {return [...prevState,newDoc]});
        globalCtx.raiseAlert("File uploaded successfully.","success");
        modalCloseHandler();
    }).catch((e) => {
        globalCtx.raiseAlert(errorMessage(e), "error");
    });
  }

  const downloadFile = (document_file,doc_file,doc_file_name) => {
    setListOfDownloadForms(prevState => prevState.map((item) => item.id === document_file.id ? {...item,isDownloading:true} : item));
    axios
    .get(`/api/operations/download-docs?doc_file=${doc_file}&doc_file_name=${doc_file_name}`, {
      headers: { Authorization: "Bearer " + authCtx.authTokens.access },responseType: 'blob'
    })
    .then((blob) => {
        // RCMC-12 (ticket 14): nothing left on the page (v1.2 left an empty, unnamed link); shared since ticket 22
        saveFile(blob.data, doc_file_name);
        setListOfDownloadForms(prevState => prevState.map((item) => item.id === document_file.id ? {...item,isDownloading:undefined} : item));
        // Ticket 22 (Q35): say it is done, as the details page does (v1.2 said nothing)
        globalCtx.raiseAlert(`File downloaded — ${doc_file_name}.`, "success");
    })
    .catch((e) => {
        globalCtx.raiseAlert(errorMessage(e), "error");
        setListOfDownloadForms(prevState => prevState.map((item) => item.id === document_file.id ? {...item,isDownloading:undefined} : item));
    });
  }


  const deleteFile = (document_file) => {
    setListOfDownloadForms(prevState => prevState.map((item) => item.id === document_file.id ? {...item,isDeleting:true} : item));
    axios.delete(`/api/user/download-form-view/${document_file.id}`,{
        headers:{Authorization: "Bearer " + authCtx.authTokens.access }
    }).then(response => {
        globalCtx.raiseAlert("Successfully deleted the file!","success");
        setListOfDownloadForms(prevState => prevState.filter(item => item.id !== document_file.id).map((item,index) => {item.sno = index+1;return item}));
    }).catch((e) => {
        globalCtx.raiseAlert(errorMessage(e), "error");
    });
  }

  const modalCloseHandler = () => {
    setIsAddNewModalVisible(false);
    inputRef.current.value = "";
    setSelectedDocument();
  }

  const documentBody = (doc) => {
    return (
        <div style={{display:"flex",alignItems:"center"}}>
            {doc.document_name.endsWith(".pdf") ? <FaFilePdf style={{marginRight:"10px",color:"#E74C3C",width:"16px"}} /> : doc.document_name.endsWith(".xlsx") || doc.document_name.endsWith(".csv") ? <FaFileExcel  style={{marginRight:"10px",color:"var(--portal-success)",width:"16px"}} /> : <IoDocumentText  style={{marginRight:"10px",color:"#ddd",width:"16px"}} />}
            <div>
                <div style={{fontWeight:"bold",fontSize:"0.9rem",color:'#2c3e50',overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",flex:"1"}}>{doc.document_name}</div>
                <div style={{fontSize:'0.7rem'}}>{doc.document_size}</div>
            </div>
        </div>
    )
  }

  // RCMC-12 (ticket 14): the "Download" column. The icon-only buttons are named for the document they act on (the
  // icons are decorative); a statutory form also links to the website's accessible HTML edition, in place (no new
  // window), after the buttons, so v1.2's download / delete pair keeps its look on every row. Download and delete
  // behave as in v1.2, and delete still shows only for a superuser.
  const downloadTemplate = (doc) => {
    const edition = htmlEdition(doc.document_name);
    return (
        <div style={{display:"flex", alignItems:"center"}}>
            {/* Ticket 22 (Q35): busy without being disabled, so focus stays on it while the file comes (Util/busyButton.js) */}
            <Button aria-label={downloadButtonName(doc)} className={styles['download-button']} {...busyButtonProps(doc.isDownloading !== undefined, () => {downloadFile(doc,doc.document,doc.document_name);})}>{!doc.isDownloading && <IoMdDownload size={18} aria-hidden="true" />}</Button>
            {authCtx.user.is_superuser && <Button aria-label={`Delete ${doc.document_name}`} loading={doc.isDeleting !== undefined} onClick={() => {deleteFile(doc)}} className={styles['delete-button']}>{!doc.isDeleting && <MdDelete size={18} aria-hidden="true" />}</Button>}
            {edition && <a href={edition.href} className={styles['html-version']}>HTML version<span className="visually-hidden"> of {edition.form}</span></a>}
        </div>
    )
  }

  // RCMC-04 (ticket 16): `table-page` lets the page flow below 768px, `table-page-grows` below desktop width (App.css)
  return (
    <div className="table-page table-page-grows" style={{ padding: "10px", maxHeight: "100vh", overflow: "hidden" }}>
        {/* RCMC-04 (ticket 16): the page name and (Operations) Upload Docs wrap when the window is too narrow for one line */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", justifyContent: "space-between", alignItems:"center",paddingBottom:"10px" }}>
            {/* RCMC-08 (ticket 11): the page name is the page's one h1, same look */}
            <PageHeading icon={<IoDownload style={{marginRight:"5px"}} />}>Forms & FAQs</PageHeading>
            {authCtx.user.is_admin && <button type="button" onClick={() => {setIsAddNewModalVisible(true);}} style={{ background: "var(--portal-primary)", border:"none",boxShadow:"rgba(0, 0, 0, 0.24) 0px 3px 8px",borderRadius:"5px",color:"var(--portal-on-primary)", padding: "8px 20px",display:'flex',alignItems:"center"}}>
                <IoCloudUpload size={15} style={{marginRight:"10px"}}/>Upload Docs
            </button>}
        </div>
        {/* <div style={{display: "grid",gridTemplateColumns:"repeat(4, minmax(0, 1fr))",gap:"10px"}}>
            {listOfDownloadForms.length === 0 && <div style={{marginTop:"10px"}}>No forms available!</div>}
            {listOfDownloadForms.map((item) => (
                    <div key={item.id} class={styles["box"]}>
                        <div class={styles["card"]}>
                            <div class={styles["card__corner"]} style={item.document_name.endsWith(".pdf") ? {backgroundColor:"#EC7063"}:item.document_name.endsWith(".xlsx") || item.document_name.endsWith(".csv") ? {backgroundClip:"#48C9B0"} : {backgroundClip:"#ddd"}}>
                                <div class={styles["card__corner-triangle"]}></div>
                            </div>
                        </div>
                        <div style={{display:"flex",alignItems:"center"}}>
                        {item.document_name.endsWith(".pdf") ? <FaFilePdf style={{marginRight:"10px",color:"#E74C3C",width:"16px"}} /> : item.document_name.endsWith(".xlsx") || item.document_name.endsWith(".csv") ? <FaFileExcel  style={{marginRight:"10px",color:"#48C9B0",width:"16px"}} /> : <IoDocumentText  style={{marginRight:"10px",color:"#ddd",width:"16px"}} />}
                            <div>
                                <div style={{fontWeight:"bold",fontSize:"0.9rem",color:'#2c3e50',overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",flex:"1"}}>{item.document_name}</div>
                                <div style={{fontSize:'0.7rem'}}>{item.document_size}</div>
                            </div>
                        </div>
                        <div style={{position:"absolute",bottom:"0",paddingBottom:"10px",display:"flex", alignItems:"center"}}>
                            <Button loading={item.isDownloading !== undefined} className={styles['download-button']}  onClick={() => {downloadFile(item,item.document,item.document_name);}}>{!item.isDownloading && <IoMdDownload size={18} />}</Button>
                            {authCtx.user.is_admin && <Button loading={item.isDeleting !== undefined} onClick={() => {deleteFile(item)}} className={styles['delete-button']}>{!item.isDeleting && <MdDelete size={18} />}</Button>}
                        </div>
                    </div>
            ))}
        </div> */}
        <CustomCard>
            {/* RCMC-10 (ticket 13): named by the h1, valid row groups, scoped headers, sortable headers say so, no
                clickable rows. RCMC-12 (ticket 14): the last column is headed "Download" (v1.2: empty). RCMC-04 (ticket 16): no fixed
                50rem minimum; too wide for the window, the table scrolls in its own region */}
            <DataTable ref={dt} value={listOfDownloadForms} paginator rows={selectedRowCount} stripedRows size="small" tableStyle={{ fontSize: "14px" }} pt={tablePassThrough()}>
                <Column field="sno" {...columnProps("SNo", { sortable: true })}></Column>
                <Column field="document_name" {...columnProps("Name", { sortable: true })}></Column>
                {/* RCMC-04 (ticket 16): below desktop width the long descriptions keep a readable column (16rem) — the table
                    then scrolls further in its region — instead of one word a line (rows 272px tall at 320). Only there
                    (DownloadForms.module.css): at 1280 a minimum, even one below the column's width, moves the columns. */}
                <Column field="document_description" {...columnProps("Description", { sortable: true })} className={styles['description-column']}></Column>
                <Column field="document_size" header="Size"></Column>
                <Column columnKey="download" {...columnProps("Download")} body={downloadTemplate}></Column>
            </DataTable>
        </CustomCard>
        {authCtx.user.is_admin && <Dialog header="Upload Docs" visible={isAddNewModalVisible} style={{ width: "50vw" }} onHide={modalCloseHandler} {...uploadDialogIsolation}>
            <div style={{marginBottom:"15px",fontSize:"0.8rem",display:"flex",alignItems:"center"}}>
                <div onClick={() => {inputRef.current.click();}} style={{borderRadius:"8px",padding:"10px 20px",background: "var(--portal-primary)",color:"var(--portal-on-primary)", display:'flex',alignItems:"center"}}><IoCloudUpload style={{marginRight:"10px"}} />Upload Docs ( Upto 1 MB )</div>
                <div style={{marginInline:"10px",textOverflow:"ellipsis",overflow:"hidden"}}>{selectedDocument && selectedDocument.name}</div>
                {selectedDocument &&  <MdDelete onClick={() => {inputRef.current.value = "";setSelectedDocument()}} className={styles['delete-selected-file']} size={16} />}
                <input ref={inputRef} style={{display:"hidden"}} type="file" onChange={(e) => {if(e.target.files[0]) {setSelectedDocument(e.target.files[0])}}} hidden/>
            </div>
            <div style={{marginBottom:"15px",fontSize:"0.8rem",display:"flex",alignItems:"center"}}>
                <FloatLabel style={{width:"100%"}}>
                    <InputTextarea id="document_description" value={selectedDocumentDescription} onChange={(e) => setSelectedDocumentDescription(e.target.value)} rows={5} cols={30} />
                    <label htmlFor="document_description">Description</label>
                </FloatLabel>
            </div>
            <Button label="Upload" raised style={{width:"100%"}} onClick={uploadNewDocHandler} />
        </Dialog>}
    </div>
  )
}

export default DownloadForms
